import { ApiError } from '../client';
import { ExclusionListUsersSchema, type ExcludedUser } from '../schemas/exclusionList';

// Accounts a phone can resolve to: the two seeded exclusions plus one «Usuario» the search
// fixtures also know (`mocks/users.ts`), so a dev session can look someone up and then exclude them.
const DIRECTORY: readonly { user_id: number; name: string; phone: string }[] = [
  { user_id: 330148, name: 'Lucía Fernández Castro', phone: '+34612348902' },
  { user_id: 330201, name: 'Ana López Marín', phone: '+34600111001' },
  { user_id: 330202, name: 'Bea Ruiz Soler', phone: '+34600111002' },
];

// Fixed instants, never `new Date()`: a value that moves inside a fixture is a test that fails at
// midnight.
const AMAZON_SEED: readonly (readonly [number, string])[] = [
  [330202, '2026-10-02T10:15:00'],
  [330201, '2026-10-01T09:30:00'],
];
const ADDED_AT = '2026-10-08T12:00:00';

// One store per carrier slug, not a constant: the list has to MOVE when the dev adds or removes
// someone, or neither write can be exercised by hand under VITE_USE_MOCK_API.
let excludedSince = new Map<string, Map<number, string>>();

// Exported for tests: module state outlives an `it`, so a suite that writes before it reads would
// otherwise depend on its own declaration order.
export function resetExclusionListMock(): void {
  excludedSince = new Map([['amazon', new Map(AMAZON_SEED)]]);
}

resetExclusionListMock();

const latency = () => new Promise((resolve) => setTimeout(resolve, 100));

function listOf(slug: string): Map<number, string> {
  let list = excludedSince.get(slug);
  if (list === undefined) {
    list = new Map();
    excludedSince.set(slug, list);
  }
  return list;
}

export async function fetchExclusionListUsersMock(slug: string): Promise<ExcludedUser[]> {
  await latency();
  const rows = [...listOf(slug).entries()]
    .map(([userId, since]) => {
      const account = DIRECTORY.find((entry) => entry.user_id === userId);
      if (account === undefined) throw new Error(`mock directory has no account ${String(userId)}`);
      return { ...account, since };
    })
    // Most recent first, as the delivered read orders them.
    .sort((a, b) => (a.since < b.since ? 1 : -1));
  // Through the same parse as the real read, so the mock cannot drift from the wire shape.
  return ExclusionListUsersSchema.parse({ users: rows }).users;
}

export async function addUserToExclusionListMock(slug: string, phone: string): Promise<void> {
  await latency();
  const account = DIRECTORY.find((entry) => entry.phone === phone);
  if (account === undefined) {
    throw new ApiError(
      400,
      'unregistered_user',
      { code: 'unregistered_user', message: 'User not found', detail: '' },
      'User not found',
    );
  }
  const list = listOf(slug);
  // Idempotent like the delivered write: a repeat keeps the original row and its `since`.
  if (!list.has(account.user_id)) list.set(account.user_id, ADDED_AT);
}

export async function removeUserFromExclusionListMock(slug: string, userId: number): Promise<void> {
  await latency();
  listOf(slug).delete(userId);
}
